import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';

import { IPositionWithCandidates } from '@types';
import { ICandidatesViewState, useCollapsedGroups, useIsPhone } from '@hooks';
import { CandidateDetailsAction } from '@constants';
import { CandidateCardList } from './components/CandidateCardList';
import { CandidatesTable } from './components/CandidatesTable';
import { buildCandidateGroups, buildCandidateLookups } from './grouping';

interface ICandidatesListProps {
  data: IPositionWithCandidates[];
  view: ICandidatesViewState;
  onSort: (key: string) => void;
  onCandidateClick: (id: string) => void;
  onCandidateAction: (id: string, action: CandidateDetailsAction) => void;
  emptyState: React.ReactNode;
}

export const CandidatesList: React.FC<ICandidatesListProps> = ({
  data,
  view,
  onSort,
  onCandidateClick,
  onCandidateAction,
  emptyState,
}) => {
  const isPhone = useIsPhone();
  const fold = useCollapsedGroups(Boolean(view.query.trim()));

  const lookups = useMemo(() => buildCandidateLookups(data), [data]);

  const groupedData = useMemo(
    () => buildCandidateGroups(data, view, lookups),
    [data, view, lookups],
  );

  const hasAnyCandidate = groupedData.some((group) => group.candidates.length);
  if (isEmpty(groupedData) || !hasAnyCandidate) return <>{emptyState}</>;

  const groupedByPosition =
    view.groupBy === 'position' || view.groupBy === 'workstream-position';

  const positionNameOf = (candidateId: string) =>
    groupedByPosition
      ? undefined
      : lookups.positionTitlesById.get(candidateId)?.[0];

  return isPhone ? (
    <CandidateCardList
      groups={groupedData}
      positionNameOf={positionNameOf}
      onCandidateClick={onCandidateClick}
      onCandidateAction={onCandidateAction}
      fold={fold}
    />
  ) : (
    <CandidatesTable
      groups={groupedData}
      lookups={lookups}
      view={view}
      positionNameOf={positionNameOf}
      onSort={onSort}
      onCandidateClick={onCandidateClick}
      onCandidateAction={onCandidateAction}
    />
  );
};
